<template>
    <div>
        <!-- Profile dropdown -->
        <Menu
            v-if="authStore.userData"
            v-slot="{ open }"
            as="div"
            class="relative">
            <MenuButton class="-m-1.5 flex p-1.5">
                <span class="sr-only">Open user menu</span>
                <div class="relative">
                    <img
                        v-if="authStore.userData.image_url"
                        class="size-8 object-cover rounded-full bg-gray-50"
                        :src="authStore.userData.image_url"
                        alt="">
                    <Icon
                        v-else
                        name="heroicons:user-circle"
                        class="h-8 w-8 text-gray-400 dark:text-gray-300" />
                    <span
                        v-if="versionMismatch"
                        class="w-2 h-2 bg-danger-500 block absolute bottom-0 right-0 rounded-full" />
                </div>
                <span
                    class="hidden lg:flex lg:items-center ml-4 text-left">
                    <span class="inline-flex flex-col gap-1">
                        <span
                            class="block text-sm font-semibold leading-none text-gray-900 dark:text-gray-100 max-w-40 truncate text-ellipsis"
                            aria-hidden="true">{{ authStore.userData.fullname }}</span>
                        <span
                            class="inline-flex gap-1 items-center leading-[1.5] text-sm text-gray-400 dark:text-gray-200 max-w-40 truncate text-ellipsis">
                            <span
                                v-if="authStore.userData.organisation_type === 'association'"
                                :title="$t('organisations.form.general.types.' + authStore.userData.organisation_type)"
                                class="inline-flex items-baseline">
                                <Icon
                                    class="size-4"
                                    name="heroicons:building-library" />
                            </span>
                            <span
                                class="text-xs truncate"
                                :title="authStore.userData.organisation_name">
                                {{ authStore.userData.organisation_name }}
                            </span>
                        </span>
                    </span>
                    <Icon
                        name="heroicons:chevron-down"
                        class="ml-4 h-5 w-5 text-gray-400 transition-transform duration-150 ease-in-out"
                        :class="{'rotate-180': open}"
                        aria-hidden="true" />
                </span>
            </MenuButton>
            <Transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">
                <MenuItems
                    class="absolute right-0 z-40 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white dark:bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div class="px-4 py-2 text-sm text-gray-700 dark:text-gray-100">
                        <div
                            v-if="!changeOrganisation"
                            class="flex flex-col">
                            <span class="inline-block font-medium mb-1">{{ $t('settings.activeOrganisation') }}</span>
                            <span
                                :title="authStore.userData.organisation_name"
                                class="inline-block font-bold text-base truncate">{{
                                    authStore.userData.organisation_name
                                }}</span>
                            <span
                                v-if="authStore.userData.additional_info"
                                :title="authStore.userData.additional_info"
                                class="inline-block text-xs truncate">{{ authStore.userData.additional_info }}</span>
                        </div>

                        <div
                            v-if="userData &&
                                userData.organisationUsers &&
                                userData.organisationUsers.length > 1"
                            class="flex flex-col">
                            <BaseSelect
                                v-if="changeOrganisation"
                                :model-value="userData.current_organisation_id"
                                :label="$t('settings.activeOrganisation')"
                                :name="$t('settings.activeOrganisation')"
                                display-key="name"
                                additional-display-key="additionalInfo"
                                :placeholder="$t('settings.activeOrganisation')"
                                :options="userData.organisationUsers?.map(o => o.organisation)"
                                @update:model-value="setActiveOrganisation" />

                            <button
                                class="text-primary text-xs mt-2 self-end"
                                @click="changeOrganisation = !changeOrganisation">
                                {{ (changeOrganisation) ? 'zurück' : 'wechseln' }}
                            </button>
                        </div>
                    </div>

                    <div class="py-1">
                        <NuxtLink
                            :to="useLocalePath()('/settings/users/' + authStore.userData.id + '/userBaseData')">
                            <BaseMenuItem icon="heroicons:user">
                                {{ $t('menu.myProfile') }}
                            </BaseMenuItem>
                        </NuxtLink>
                        <NuxtLink
                            :to="useLocalePath()('/settings/organisations/' + authStore.userData.current_organisation_id + '/organisationBaseData')">
                            <BaseMenuItem icon="heroicons:building-office">
                                <span class="whitespace-nowrap">{{ $t('menu.myOrganisation') }}</span>
                            </BaseMenuItem>
                        </NuxtLink>
                        <NuxtLink
                            v-if="$can('viewAny', 'users')"
                            :to="useLocalePath()('/settings/users/')">
                            <BaseMenuItem icon="heroicons:users">
                                <span class="whitespace-nowrap">{{ $t('menu.myUsers') }}</span>
                            </BaseMenuItem>
                        </NuxtLink>

                        <slot name="additionalMenuItems" />
                    </div>

                    <BaseMenuItem
                        icon="heroicons:information-circle"
                        @click="showUserInfoModal = true">
                        <template #icon>
                            <span class="relative">
                                <Icon
                                    name="heroicons:information-circle"
                                    class="w-5 h-5" />

                                <span
                                    v-if="versionMismatch"
                                    class="w-2 h-2 bg-danger-500 block absolute bottom-0 right-0 rounded-full" />
                            </span>
                        </template>
                        {{ $t('menu.information') }}
                    </BaseMenuItem>
                    <NuxtLink
                        v-if="hasEscapeRoomFeature"
                        :to="useLocalePath()('/secret/dungeon')">
                        <BaseMenuItem
                            icon="fa6-solid:dungeon"
                            icon-class="ml-6">
                            {{ $t('menu.secret') }}
                        </BaseMenuItem>
                    </NuxtLink>
                    <BaseMenuItem
                        v-if="hasDarkFeature"
                        class="cursor-default"
                        @click="$event => $event.preventDefault()">
                        <template #icon>
                            <BaseSwitch
                                v-model="isDark"
                                name="dark-mode"
                                :icons="['heroicons:sun', 'heroicons:moon']" />
                        </template>
                        Darkmode
                    </BaseMenuItem>
                    <BaseMenuItem
                        v-if="hasMultiLanguageFeature"
                        class="cursor-default"
                        @click="$event => $event.preventDefault()">
                        <template #icon>
                            <BaseSwitch
                                v-model="languageToggle"
                                name="locale"
                                :icons="['circle-flags:de', 'circle-flags:en']" />
                        </template>
                        {{ $t(`locales.${lang}`) }}
                    </BaseMenuItem>
                    <BaseMenuItem
                        icon="heroicons:arrow-left-on-rectangle"
                        @click="logout">
                        {{ $t('menu.logout') }}
                    </BaseMenuItem>
                </MenuItems>
            </Transition>
        </Menu>
        <UserInfoModal v-model="showUserInfoModal" />
    </div>
</template>

<script setup>
import {useNuxtApp} from "nuxt/app"
import {useLanguage} from "@/composables/useLanguage"
import {useNotification} from "@/composables/useNotification"
import {useAuthStore} from "@/store/auth"
import {$lara} from "@/utils/$larafetch"

const loading = ref(false)
const showUserInfoModal = ref(false)
const authStore = useAuthStore()
const userData = computed(() => authStore.userData)
const {versionIsUpToDate, versionMismatch} = useAppVersion()
const route = useRoute()
const router = useRouter()
const changeOrganisation = ref(false)
const {toggle: languageToggle, lang} = useLanguage()

const logout = async () => {
    loading.value = true
    await authStore.logout()
    loading.value = false
}

async function setActiveOrganisation(value) {
    if (userData.value.current_organisation_id !== value) {
        userData.value.current_organisation_id = value

        $lara.put(useNuxtApp().$apiRoute("spa.settings.users.update", {user: userData.value.id}), {
            body: userData.value
        }).then(response => {
            authStore.getAuthData()

            // reload the app to reload page data
            reloadNuxtApp({force: true})
        }).catch(error => {
            const notification = useNotification()
            const app = useNuxtApp()
            if (error && error.response && error.response.status === 422) {
                notification.error(app.$i18n.t('invalidRequestData'))
            } else if (error && error.response && error.response.status === 404) {
                notification.error(app.$i18n.t('modelDoesNotExist'))
            }
        })
    }
}

const hasDarkFeature = useFeature('darkMode')
const hasEscapeRoomFeature = useFeature('escapeRoom')
const hasMultiLanguageFeature = useFeature('multiLanguage')

let isDark = null

if (useFeature('darkMode')) {
    isDark = useDark({
        disableTransition: false

    })
}
</script>

<style scoped>

</style>